import { useMemo } from "react";
import { useMediaQuery } from "@mui/material";

const wholeDollarFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const formatWholeDollar = (value) => wholeDollarFormatter.format(Number(value) || 0);

const formatIsoDate = (value) => {
    if (!value) return "";
    const date = typeof value === "string" ? new Date(value) : value;
    return Number.isNaN(date?.getTime?.()) ? "" : date.toISOString().split("T")[0];
};

const useRevenueLinesFlatColumns = () => {
    const isMobile = useMediaQuery("(max-width:600px)");

    return useMemo(() => [
        {
            field: 'opportunityName',
            headerName: 'Name',
            flex: 1,
            renderCell: ({row: { opportunityId, opportunityName } }) => <a href={`/opportunities/${opportunityId}`}>{opportunityName}</a>,
        },
        // Conditionally show/hide columns
        ...(isMobile ? [] : [
            {
                field: "customer",
                headerName: "Customer",
                flex: 1,
            },               
            {
                field: "owner",
                headerName: "Owner",
                flex: 1,
                renderCell: ({ row: { owner } }) => owner ? `${owner.firstName} ${owner.lastName}` : 'Unassigned',
                valueGetter: (params) => `${params.firstName} ${params.lastName}` 
            },
            {
                field: "projectManager",
                headerName: "Project Manager",
                flex: 1,
            },                     
            {
                field: "projectNumber",
                headerName: "Project #",
                flex: 1,
            },
            {
                field: "quoteNumber",
                headerName: "Quote #",
                flex: 1,
            },
            {
                field: "productGroup",
                headerName: "Product Group",
                flex: 1,
            },
            {
                field: "amount",
                headerName: "Revenue (Non-Weighted)",
                flex: 1,   
                renderCell: ({ row: { amount }}) => amount ? formatWholeDollar(amount) : '$0',
            },            
            {
                field: "weightedAmount",
                headerName: "Revenue (Weighted)",
                flex: 1,   
                renderCell: ({ row: { weightedAmount }}) => weightedAmount ? formatWholeDollar(weightedAmount) : '$0'
            },
            {
                field: "marginPercentage",
                headerName: "Margin %",
                flex: 1,
                renderCell: ({ row: { marginPercentage } }) => {
                    if (marginPercentage === null || typeof marginPercentage === "undefined" || marginPercentage === "") {
                        return "";
                    }

                    const numericMarginPercentage = Number(marginPercentage);
                    const displayMarginPercentage = Number.isNaN(numericMarginPercentage)
                        ? marginPercentage
                        : Math.round(numericMarginPercentage);

                    return (
                        <label className={`badge ${numericMarginPercentage < 0 ? 'bg-light-danger' : numericMarginPercentage <= 10 ? 'bg-light-warning' : numericMarginPercentage <= 30 ? 'bg-light-primary' : 'bg-light-success'}`} style={{minHeight: '75%', minWidth: '75%', padding: "0", lineHeight: '3', fontSize: '14px'}}>{displayMarginPercentage}</label>
                    );
                },                               
            },
            {
                field: "marginAmount",
                headerName: "Margin (Weighted)",
                flex: 1,
                renderCell: ({ row: { marginAmount } }) => {
                    return (
                        marginAmount ? marginAmount < 0 ?
                        <label className={`badge bg-light-danger`} style={{minHeight: '75%', minWidth: '75%', padding: "0", lineHeight: '3', fontSize: '14px'}}>{formatWholeDollar(marginAmount) }</label>
                        : formatWholeDollar(marginAmount) : '$0'
                    );
                },                                  
            },
            {
                field: "shipDate",
                headerName: "Ship Date",
                renderCell: ({ row: { shipDate } }) => formatIsoDate(shipDate),
                flex: 1,           
            },
            // {
            //     field: "closeDate",
            //     headerName: "Close Date",
            //     renderCell: ({ row: { closeDate } }) => {
            //         if (!closeDate) return "";
            //         const date = typeof closeDate === "string" ? new Date(closeDate) : closeDate;
            //         return date.toISOString().split("T")[0]; // 'YYYY-MM-DD'
            //     },
            //     flex: 1,           
            // },            
            {
                field: "state",
                headerName: "State",
                flex: 1,
            },            
            {
                field: "salesStage",
                headerName: "Stage",
                flex: 1,
                renderCell: ({ row: { salesStage } }) => {
                    return (
                        <label className={`badge  ${salesStage === '01 - Lead' ? 'bg-light-primary' : salesStage === '08 - Won' ? 'bg-light-success' :salesStage === '05 - Committee Approval' ? 'bg-light-warning' : salesStage === '04 - Internal Revision' ? 'bg-light-danger' : salesStage === '06 - Quote Sent' ? 'bg-light-primary' : salesStage === '02 - Identified' ? 'bg-light-dark' : salesStage === '03 - Committee' ? 'bg-light-secondary' : salesStage === "07 - External Revision" ? 'bg-light-danger' : 'bg-light-info'}`} style={{minHeight: '75%', minWidth: '75%', padding: "0", lineHeight: '3', fontSize: '14px'}}>{salesStage}</label>
                    );
                },
            },
        ]),
        {
            field: "bookedOrdersStage",
            headerName: "Booked Orders Stage",
            flex: 1,          
            renderCell: ({ row: { bookedOrdersStage } }) => {
                return (
                    <label className={`badge  ${bookedOrdersStage === '01 - Assign Resources' ? 'bg-light-primary' : bookedOrdersStage === '07 - Revenue Taken' ? 'bg-light-success' :bookedOrdersStage === '04 - Shipped' ? 'bg-light-warning' : bookedOrdersStage === '05 - Installed' ? 'bg-light-danger' : bookedOrdersStage === '06 - Commissioned' ? 'bg-light-danger' : bookedOrdersStage === '02 - Identified' ? 'bg-light-dark' : bookedOrdersStage === '03 - Committee' ? 'bg-light-secondary' : bookedOrdersStage === "07 - External Revision" ? 'bg-light-danger' : 'bg-light-info'}`} style={{minHeight: '75%', minWidth: '75%', padding: "0", lineHeight: '3', fontSize: '14px'}}>{bookedOrdersStage}</label>
                );
            },
        },
    ], [isMobile]);
};

export default useRevenueLinesFlatColumns
